import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    cancelInterpretation,
    confirmInterpretation,
} from "../../../api/interpretation";
import BottomNavigationButtons from "../../../components/BottomNavigationButtons";
import SingleGraphView from "../../../components/interpretation/GraphsGrid/SingleGraphView";
import InterpretationDropdown from "../../../components/interpretation/InterpretationDropdowns";
import {
    InterpretationGraphs,
    InterpretationMainGraph,
} from "../../../components/interpretation/InterpretationGraphs";
import InterpretationTables from "../../../components/interpretation/InterpretationTables";
import QuickView from "../../../components/interpretation/QuickView";
import { useAuth } from "../../../context/AuthContext";
import { GraphsProvider } from "../../../context/GraphsProvider";
import { useInterpretationContext } from "../../../context/InterpretationContext";
import useConfirm from "../../../hooks/useConfirm";
import {
    useInterpretationData,
    useInterpretationGraphs,
    useInterpretationTable,
} from "../../../hooks/useInterpretationData";
import usePrompt from "../../../hooks/usePrompt";
import { useReport } from "../../../hooks/useReport";
import useTestData from "../../../hooks/useTestData";
import useTestId from "../../../hooks/useTestId";
import { ROUTES } from "../../config";
import { useGraphEnlarge } from "../../../context/GraphEnlargeContext";

export enum InterpretationLevel {
    Primary = "primary",
    Secondary = "secondary",
}

export default function Interpretation() {
    const { isAllowed } = useAuth();
    const { value: interpretationData } = useInterpretationData();
    const testId = useTestId();
    const navigate = useNavigate();
    const { value: testData, mutate: mutateTestData } = useTestData();
    const { show } = useConfirm();
    const {
        setData: setInterpretationData,
        setInitialState: setInitialInterpretationState,
        isInterpretationDirty,
    } = useInterpretationContext();
    const { mutate: revalidateReport, report } = useReport({
        disableFetch: Boolean(
            testData?.navbarStatus && testData.navbarStatus < 2
        ),
    });
    useEffect(() => {
        if (testData?.testStatus === "Not completed") {
            mutateTestData(
                (prev) => prev ? ({
                    ...prev,
                    testStatus: "Interpreted viewed" as const,
                }) : null,
                false
            );
        }
    }, []);

    const { graphIdToEnlarge, setGraphIdToEnlarge } = useGraphEnlarge();
    const [interpretationLevel, setInterpretationLevel] =
        useState<InterpretationLevel>(InterpretationLevel.Primary);
    const [shouldBlock, setShouldBlock] = useState(true);

    const onConfirm = async () => {
        setInterpretationData(undefined);
        await confirmInterpretation(testId);
        mutateTestData();
        if (report) {
            revalidateReport();
        }
    };

    const onCancel = async () => {
        setInterpretationData(undefined);
        await cancelInterpretation(testId);
    };

    usePrompt({
        isDirty:
            isAllowed("Interpretation confirmation") &&
            isInterpretationDirty &&
            shouldBlock,
        onConfirm,
        onCancel,
        isNavigateOnCancel: true,
    });

    const graphValues = useInterpretationGraphs({
        interpretationLevel,
        interpretationData,
    });

    const {
        graphs,
        loading: graphsLoading,
        error: graphsError,
        mutate: mutateGraphs,
    } = graphValues;

    const {
        value: { tables, withSymptoms, withECG } = {},
        loading: tablesLoading,
        error: tablesError,
        refetch: refetchTables,
    } = useInterpretationTable({ interpretationLevel, interpretationData });

    const isSingleFieldLayout = useMemo(() => graphs?.length === 5, [graphs]);

    const triggerRefetch = useCallback(() => {
        mutateGraphs();
        refetchTables();
    }, [mutateGraphs, refetchTables]);

    const handleInterpretationChange = async () => {
        if (!testData) {
            alert("An Error Occured");
            return;
        }

        const confirmedByServer = await confirmInterpretation(testId);
        setShouldBlock(false);
        setInitialInterpretationState(undefined);
        setInterpretationData(undefined);
        if (confirmedByServer?.navbar_status !== 2) {
            alert("Error confirming interpretation");
            throw new Error("Error confirming interpretation");
        }
    };

    const onConfirmClick = () => {
        const isLimitingSystemSelected =
            interpretationData?.limiting_system &&
            !interpretationData?.other_limiting_systems
                .filter((i) => i.is_none)
                .map((i) => i.label)
                .includes(interpretationData.limiting_system);
        const limitingSystemOptional =
            interpretationData?.detailed_interpretation &&
            interpretationData?.other_detailed_interpretations
                .filter((i) => i.is_disable_none_detailed)
                .map((i) => i.label)
                .includes(interpretationData.detailed_interpretation);

        const isValid = isLimitingSystemSelected || limitingSystemOptional;

        const navigateToReport = () => {
            navigate(`/test/${testId}/${ROUTES.REPORT.path}`);
        };

        const isReportDisabled = testData?.navbarStatus === 1;
        if (!isValid) {
            show({
                title: "Interpretation selection is required to confirm",
                confirmText: "OK",
                hideCancelButton: true,
            });
        } else if (isInterpretationDirty || isReportDisabled) {
            show({
                onConfirm: async () => {
                    await handleInterpretationChange();
                    mutateTestData();
                    if (report) {
                        revalidateReport();
                    }
                    navigateToReport();
                },
                onCancel: () => {},
            });
        } else {
            navigateToReport();
        }
    };

    return (
        <GraphsProvider contextValue={graphValues}>
            <div className="flex xl:pl-5 gap-1 xl:gap-6 pb-bottomPanel">
                <div className="h-full w-leftSidebar shrink-0">
                    <QuickView />
                </div>
                <div className="h-full w-full flex flex-col">
                    <InterpretationDropdown
                        triggerRefetch={triggerRefetch}
                        interpretationLevel={interpretationLevel}
                        setInterpretationLevel={setInterpretationLevel}
                    />
                    {graphIdToEnlarge && graphs ? (
                        <>
                            <SingleGraphView
                                graphIdToEnlarge={graphIdToEnlarge}
                                setGraphIdToEnlarge={setGraphIdToEnlarge}
                                disabled
                            />
                            <div className="w-full flex justify-end mt-2">
                                <button
                                    className="btn-primary w-[200px]"
                                    onClick={() => setGraphIdToEnlarge(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div
                                className={`h-full w-full flex mb-4 items-start ${
                                    isSingleFieldLayout
                                        ? "flex-col"
                                        : "flex-row"
                                } gap-x-2 2xl:gap-x-8 gap-y-2 2xl:gap-y-8`}
                            >
                                <div className="flex flex-row justify-between">
                                    <InterpretationTables
                                        tables={tables}
                                        loading={tablesLoading}
                                        error={tablesError}
                                        withSymptoms={Boolean(withSymptoms)}
                                        withECG={Boolean(withECG)}
                                    />
                                    <InterpretationMainGraph
                                        graph={graphs?.[0]}
                                        show={isSingleFieldLayout}
                                        loading={graphsLoading}
                                        error={graphsError}
                                        refetch={triggerRefetch}
                                    />
                                </div>
                                <InterpretationGraphs
                                    graphs={graphs}
                                    loading={graphsLoading}
                                    error={graphsError}
                                    asGrid={!isSingleFieldLayout}
                                    refetch={triggerRefetch}
                                />
                            </div>
                            <BottomNavigationButtons
                                isConfirmDisabled={
                                    !isAllowed("Interpretation confirmation")
                                }
                                isFixed
                                cancelText="Back"
                                cancelLinkPath={`/test/${testId}/${ROUTES.DATA_INPUT.path}`}
                                onConfirmClick={onConfirmClick}
                                disclaimerText="This interpretation should not be used clinically unless approved and signed by a physician"
                            />
                        </>
                    )}
                </div>
            </div>
        </GraphsProvider>
    );
}
